import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// REDUX
import { useAppSelector } from '../../../store';

// PACKAGES
import { ErrorMessage, Field, FastField, Form, Formik } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../../components/Button';
import FileUpload from '../../../components/FileUpload';
import SingleSelect from '../../../components/SingleSelect';
import ExistingCertification from '../../../components/ExistingCertification';
import DateDayExpiryInput from '../../../components/DateDayExpiryInput';

// TYPES
import { VisitorCertificationTypes } from './certificationVisitor';
import {
	AccountExistingCertificationsCertification,
	AccountJoinOrganisationCertification,
	AccountJoinOrganisationCommand,
	DomainCertificationType,
	DomainProfileType,
	OrganisationSearchOrganisationSearchSummary
} from '../../../api/models';

interface PartOneTypes {
	setFormSection: React.Dispatch<React.SetStateAction<string>>;
	setFormData: React.Dispatch<React.SetStateAction<VisitorCertificationTypes>>;
	formData: VisitorCertificationTypes;
	existingCertification: AccountExistingCertificationsCertification[];
	loading: boolean;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	setFormErrorMessage: React.Dispatch<React.SetStateAction<string>>;
	setShowError: React.Dispatch<React.SetStateAction<boolean>>;
	organisationData: OrganisationSearchOrganisationSearchSummary | undefined;
	profileData:
		| {
				label?: string;
				value?: DomainProfileType;
		  }
		| undefined;
}

const PartOne: React.FC<PartOneTypes> = ({
	setFormSection,
	setFormData,
	formData,
	loading,
	existingCertification,
	setIsLoading,
	setFormErrorMessage,
	setShowError,
	organisationData,
	profileData
}) => {
	const visitorTypes = useAppSelector((state) => state.lists.visitorTypes);

	const WWCCRegFields = {
		WWCCReg: false
	};

	// Get all states from Redux store
	const states = useAppSelector((state) => state?.lists.states);

	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Set initial template
	const certificationTemplate: AccountJoinOrganisationCertification = {
		certificationType: DomainCertificationType.WorkingWithChildrenCheck,
		state: '',
		expiry: '',
		certificationNumber: '',
		createAttachments: [],
		userAcknowledgement: false
	};

	const existingWWCCRegistration =
		existingCertification &&
		existingCertification.filter(
			(item: AccountExistingCertificationsCertification) =>
				item.certificationType === DomainCertificationType.WorkingWithChildrenCheck
		);

	// Set Initial Values
	const initalVisitorsValues = {
		visitorType: formData.visitorType,
		businessName: formData ? formData.businessName : '',
		existingWWCCRegistration:
			(existingWWCCRegistration &&
				existingWWCCRegistration.map((item) => {
					return { ...item, ...WWCCRegFields };
				})) ||
			[],
		workingWithChildren: formData ? { ...formData.workingWithChildren } : { ...certificationTemplate },
		acknowledge: false,
		hasValidExisitingWWCCCertificate: false,
		volunteerStatus: 'workingWithChildren'
	};

	// Validation
	const visitorsFormValidation = Yup.object().shape({
		visitorType: Yup.string().required('Required'),
		businessName: Yup.string().required('Required'),
		workingWithChildren: Yup.object().when('volunteerStatus', {
			is: 'workingWithChildren',
			then: Yup.object().shape({
				state: Yup.string().required('Required'),
				expiry: Yup.string().required('Required'),
				createAttachments: Yup.array().min(1, 'Required').required('Required'),
				certificationNumber: Yup.string().required('Required'),
				userAcknowledgement: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
			})
		}),
		existingWWCCRegistration: Yup.array().when('volunteerStatus', {
			is: 'useExisting',
			then: Yup.array().of(
				Yup.object({
					WWCCReg: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
				})
			)
		}),
		acknowledge: Yup.bool().oneOf([true], 'Required').required('Required')
	});

	// Submit Form
	const visitorsFormSubmit = async (values: typeof initalVisitorsValues) => {
		setIsLoading(true);

		const useExistingWWCC = values.volunteerStatus === 'useExisting';

		const workingWithChildren = {
			...formData.workingWithChildren,

			state: values.workingWithChildren.state,
			expiry: values.workingWithChildren.expiry,
			certificationNumber: values.workingWithChildren.certificationNumber,
			createAttachments: values.workingWithChildren.createAttachments,
			userAcknowledgement: values.workingWithChildren.userAcknowledgement
		};

		setFormData((prevState) => ({
			...prevState,
			visitorType: values.visitorType,
			visitPurpose: values.visitorType,
			businessName: values.businessName,
			workingWithChildren,
			acknowledge: values.acknowledge
		}));

		// When re-using an existing WWCC send no certifications, otherwise the back-end replaces the current WWCC with the blank entry
		const submitData: AccountJoinOrganisationCommand = {
			organisationIdentifier: organisationData?.identifier,
			profileType: profileData?.value,
			visitorType: values.visitorType,
			businessName: values.businessName,
			volunteerRequest: true,
			certifications: useExistingWWCC ? [] : [workingWithChildren]
		};

		const response = await apibridge.doJoinOrganisation(submitData);

		if (response.isError) {
			setShowError(true);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setFormSection('success');
		}

		setIsLoading(false);
	};

	return (
		<Formik
			initialValues={initalVisitorsValues}
			validationSchema={visitorsFormValidation}
			onSubmit={visitorsFormSubmit}
		>
			{(formik) => {
				const handleUpdateState = (field: string, val: string) => {
					formik.setFieldValue(field, val);

					setFormData((prevState) => ({ ...prevState, [field]: val }));
				};

				React.useEffect(() => {
					// Default to the existing WWCC only when at least one is still valid
					existingCertification &&
						existingCertification.some((cert) => cert.validCertification) &&
						formik.setFieldValue('volunteerStatus', 'useExisting');
				}, []);

				return (
					<Form translate="yes">
						<div className="form-group">
							<label htmlFor="purposeForVisit">1. Please confirm the purpose of your visit</label>

							<SingleSelect
								options={visitorTypes}
								name="visitorType"
								value={formik.values.visitorType}
								onChange={(val: string) => formik.setFieldValue('visitorType', val)}
							/>

							<ErrorMessage name="visitorType">
								{(error) => <div className="form-validation-error">{error}</div>}
							</ErrorMessage>
						</div>

						<div className="form-group">
							<label htmlFor="businessName">2. Please confirm where you are from (eg. business name)</label>

							<Field
								className="form-control"
								type="text"
								name="businessName"
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateState('businessName', e.target.value)}
								placeholder="Please specify... "
							/>

							<ErrorMessage name="businessName">
								{(error) => <div className="form-validation-error">{error}</div>}
							</ErrorMessage>
						</div>

						<div className="form-group">
							<label htmlFor="addLater">3. Please provide your Working with Children check details</label>

							<p className="sub-grey-text">You will need to provide this to be on site with our children</p>

							{existingCertification && existingCertification.length > 0 && (
								<>
									{/* The "use existing" option is only offered when at least one WWCC is still valid */}
									{existingCertification.some((cert) => cert.validCertification) && (
										<label
											className={
												formik.values.volunteerStatus === 'useExisting'
													? 'form-control-radio radio-selected'
													: 'form-control-radio'
											}
										>
											<Field
												name="volunteerStatus"
												type="radio"
												value={'useExisting'}
												onChange={() => {
													formik.setFieldValue('volunteerStatus', 'useExisting');
													// Clear any typed WWCC details so leftover input is not displayed or submitted after switching options
													formik.setFieldValue('workingWithChildren', { ...certificationTemplate });
													// Also reset touched state so the cleared fields do not immediately show validation errors when switching back
													formik.setFieldTouched('workingWithChildren', false);
													setFormData((prevState) => ({
														...prevState,
														workingWithChildren: { ...certificationTemplate }
													}));
												}}
											/>
											Use my existing WWCC details
										</label>
									)}

									{/* Shown when "use existing" is selected, or always when the only existing WWCCs are expired (so the user can see why they must provide new details) */}
									{formik.values.volunteerStatus === 'useExisting' ||
									!existingCertification.some((cert) => cert.validCertification) ? (
										<ExistingCertification existingWWCCRegistration={existingWWCCRegistration} />
									) : null}

									<label
										className={
											formik.values.volunteerStatus === 'workingWithChildren'
												? 'form-control-radio radio-selected'
												: 'form-control-radio'
										}
									>
										<Field name="volunteerStatus" type="radio" value={'workingWithChildren'} />
										Here are my Working With Children Check details
									</label>
								</>
							)}

							{formik.values.volunteerStatus === 'workingWithChildren' && (
								<div className="form-section-title">
									<div className="form-group">
										<label className="sub-grey-text" htmlFor="state">
											State of registration
										</label>

										<Field
											children={() => (
												<SingleSelect
													name="workingWithChildren.state"
													options={states}
													onChange={(val: string) => {
														formik.setFieldValue('workingWithChildren.state', val);
														setFormData((prevState) => ({
															...prevState,

															workingWithChildren: {
																...prevState.workingWithChildren,
																state: val
															}
														}));
													}}
													value={formik.values.workingWithChildren.state}
												/>
											)}
										/>

										<ErrorMessage name="workingWithChildren.state">
											{(error) => <div className="form-validation-error">{error}</div>}
										</ErrorMessage>
									</div>

									<div className="form-group two-col">
										<div className="sub-form-group">
											<label className="sub-grey-text" htmlFor="certificationNumber">
												WWCC Number
											</label>

											<Field
												className="form-control"
												type="text"
												name="workingWithChildren.certificationNumber"
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													formik.setFieldValue('workingWithChildren.certificationNumber', e.target.value);
													setFormData((prevState) => ({
														...prevState,

														workingWithChildren: {
															...prevState.workingWithChildren,
															certificationNumber: e.target.value
														}
													}));
												}}
												value={formik.values.workingWithChildren.certificationNumber}
											/>

											<ErrorMessage name="workingWithChildren.certificationNumber">
												{(error) => <div className="form-validation-error">{error}</div>}
											</ErrorMessage>
										</div>

										<div className="sub-form-group">
											<label className="sub-grey-text" htmlFor="expiry">
												Expiry
											</label>

											<FastField
												className="form-control"
												placeholder="MM / YYYY"
												name="workingWithChildren.expiry"
												children={() => (
													<DateDayExpiryInput
														dateTimeString={formik.values.workingWithChildren.expiry || ''}
														onChange={(val: any) => {
															formik.setFieldValue(`workingWithChildren.expiry`, val),
																setFormData((prevState) => ({
																	...prevState,
																	workingWithChildren: {
																		...prevState.workingWithChildren,
																		expiry: val
																	}
																}));
														}}
														excludePast={true}
													/>
												)}
											/>

											<ErrorMessage name="workingWithChildren.expiry">
												{(error) => <div className="form-validation-error">{error}</div>}
											</ErrorMessage>
										</div>
									</div>

									<div className="form-group">
										<Field
											children={() => (
												<>
													<FileUpload
														label="Photo of WWCC"
														name="workingWithChildren.createAttachments"
														placeholder="Choose file..."
														multiple={true}
														onChange={(val: string[], files: any[]) => {
															formik.setFieldValue('workingWithChildren.createAttachments', val);

															setFormData((prevState) => ({
																...prevState,
																workingWithChildren: {
																	...prevState.workingWithChildren,
																	createAttachments: val
																}
															}));
														}}
													/>
												</>
											)}
										/>{' '}
										<ErrorMessage name={`workingWithChildren.createAttachments`}>
											{(error) => <div className="form-validation-error">{error}</div>}
										</ErrorMessage>
									</div>

									<div className="form-group acknowledge-container mbtom-0">
										<label className="form-control-checkbox">
											<Field
												name="userAcknowledgement"
												type="checkbox"
												onChange={() => {
													formik.setFieldValue(
														`workingWithChildren.userAcknowledgement`,
														!formik.values.workingWithChildren.userAcknowledgement
													);
													setFormData((prevState) => ({
														...prevState,
														workingWithChildren: {
															...prevState.workingWithChildren,
															userAcknowledgement: !formik.values.workingWithChildren.userAcknowledgement
														}
													}));
												}}
												checked={formik.values.workingWithChildren.userAcknowledgement}
											/>

											<span>
												I have notified the Working with Children Check authority that I am working or volunteering at
												this organisation.
											</span>
										</label>

										<ErrorMessage name={`workingWithChildren.userAcknowledgement`}>
											{(error) => <div className="form-validation-error">{error}</div>}
										</ErrorMessage>
									</div>
								</div>
							)}
						</div>

						<div className="general-divider"></div>

						<div className="form-group acknowledge-container">
							<label className="form-control-checkbox">
								<Field
									name="acknowledge"
									type="checkbox"
									onChange={() => {
										formik.setFieldValue(`acknowledge`, !formik.values.acknowledge);
										setFormData((prevState) => ({
											...prevState,
											acknowledge: !formik.values.acknowledge
										}));
									}}
								/>

								<span>
									I acknowledge that all organisations that I choose to join on ChildAware will be able to view (a) my
									profile information (including any documents that I have uploaded) (b) the names of the orgnisations
									where I work or volunteer with children; and (c) my roles at these organisations. The Individual User{' '}
									<a href={`${websiteUrl}/terms-conditions-individual-user`} target="_blank">
										Terms and Conditions
									</a>{' '}
									and{' '}
									<a href={`${websiteUrl}/privacy-policy`} target="_blank">
										Privacy Policy
									</a>{' '}
									continue to apply.
								</span>
							</label>

							<ErrorMessage name="acknowledge">
								{(error) => <div className="form-validation-error">{error}</div>}
							</ErrorMessage>
						</div>

						<Button disabled={loading} type="submit" className="btn btn-primary full-width">
							Create Account
						</Button>
					</Form>
				);
			}}
		</Formik>
	);
};

export default PartOne;
